import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

// Custom hook for Supabase operations with error handling
export const useSupabase = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  // Clear error when component mounts or user changes
  useEffect(() => {
    setError(null);
  }, [user]);

  // Generic query function
  let query = async (queryFn) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await queryFn();
      if (result?.error) {
        throw result?.error;
      }
      return result;
    } catch (err) {
      setError(err?.message || 'An error occurred');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // CRUD Operations for Orders
  const orders = {
    // Fetch all orders with filters
    getAll: async (filters = {}) => {
      return query(async () => {
        let query = supabase?.from('orders')?.select(`
            *,
            vehicle:vehicles(*),
            car_house:car_houses(*),
            assigned_technician:technicians!assigned_technician_id(*),
            created_by:user_profiles!created_by_id(*),
            service_type:service_types(*),
            order_service_items(*, service_item:service_items(*))
          `)?.order('created_at', { ascending: false });

        // Apply filters
        if (filters?.status) {
          query = query?.eq('status', filters?.status);
        }
        if (filters?.priority) {
          query = query?.eq('priority', filters?.priority);
        }
        if (filters?.car_house_id) {
          query = query?.eq('car_house_id', filters?.car_house_id);
        }
        if (filters?.assigned_technician_id) {
          query = query?.eq('assigned_technician_id', filters?.assigned_technician_id);
        }
        if (filters?.date_from) {
          query = query?.gte('created_at', filters?.date_from);
        }
        if (filters?.date_to) {
          query = query?.lte('created_at', filters?.date_to);
        }

        return await query;
      });
    },

    // Get single order by ID
    getById: async (id) => {
      return query(async () => {
        return await supabase?.from('orders')?.select(`
            *,
            vehicle:vehicles(*),
            car_house:car_houses(*),
            assigned_technician:technicians!assigned_technician_id(*),
            created_by:user_profiles!created_by_id(*),
            service_type:service_types(*),
            order_service_items(*, service_item:service_items(*)),
            attachments(*),
            audit_logs(*, user:user_profiles(*))
          `)?.eq('id', id)?.single();
      });
    },

    // Create new order
    create: async (orderData) => {
      return query(async () => {
        // Remove any ID and order_number if they exist to let the database generate them
        const { id, order_number, ...cleanOrderData } = orderData;
        
        // Ensure all required fields are present
        const orderPayload = {
          ...cleanOrderData,
          created_by_id: user?.id,
          // Set defaults for required fields if not provided
          status: cleanOrderData.status || 'pending',
          priority: cleanOrderData.priority || 'normal',
          title: cleanOrderData.title || 'Neuer Auftrag',
          customer_name: cleanOrderData.customer_name || 'Unbekannt',
          customer_phone: cleanOrderData.customer_phone || '',
          // Handle vehicle_id - if not provided, we need to create a vehicle first or make it optional
          ...(cleanOrderData.vehicle_id && { vehicle_id: cleanOrderData.vehicle_id }),
          // Remove any undefined values that might cause issues
          ...Object.fromEntries(
            Object.entries(cleanOrderData).filter(([_, value]) => value !== undefined)
          )
        };
        
        
        const { data, error } = await supabase?.from('orders')?.insert([orderPayload])?.select()?.single();

        if (!error && data) {
          // Create audit log
          await supabase?.rpc('create_audit_log', {
            p_user_id: user?.id,
            p_order_id: data?.id,
            p_action: 'created',
            p_table_name: 'orders',
            p_record_id: data?.id,
            p_new_values: cleanOrderData
          });
        }

        return { data, error };
      });
    },

    // Update order
    update: async (id, updates) => {
      return query(async () => {
        // Get current data for audit log
        const { data: currentData } = await supabase?.from('orders')?.select('*')?.eq('id', id)?.single();

        const { data, error } = await supabase?.from('orders')?.update(updates)?.eq('id', id)?.select()?.single();

        if (!error && data) {
          // Create audit log
          await supabase?.rpc('create_audit_log', {
            p_user_id: user?.id,
            p_order_id: id,
            p_action: 'updated',
            p_table_name: 'orders',
            p_record_id: id,
            p_old_values: currentData,
            p_new_values: updates
          });

          // Recalculate totals if service items changed or order is completed
          if (updates?.total_amount !== undefined || updates?.status === 'completed') {
            await supabase?.rpc('calculate_order_totals', { order_uuid: id });
          }
        }

        return { data, error };
      });
    },

    // Delete order
    delete: async (id) => {
      return query(async () => {
        // Create audit log before deletion
        await supabase?.rpc('create_audit_log', {
          p_user_id: user?.id,
          p_order_id: id,
          p_action: 'deleted',
          p_table_name: 'orders',
          p_record_id: id
        });

        return await supabase?.from('orders')?.delete()?.eq('id', id);
      });
    }
  };

  // CRUD Operations for Car Houses
  const carHouses = {
    getAll: async (filters = {}) => {
      return query(async () => {
        let query = supabase?.from('car_houses')?.select(`
            *,
            manager:user_profiles!manager_id(*)
          `)?.order('name');

        if (filters?.status) {
          query = query?.eq('status', filters?.status);
        }
        if (filters?.city) {
          query = query?.eq('city', filters?.city);
        }

        return await query;
      });
    },

    create: async (carHouseData) => {
      return query(async () => {
        return await supabase?.from('car_houses')?.insert([carHouseData])?.select()?.single();
      });
    },

    update: async (id, updates) => {
      return query(async () => {
        return await supabase?.from('car_houses')?.update(updates)?.eq('id', id)?.select()?.single();
      });
    },

    delete: async (id) => {
      return query(async () => {
        return await supabase?.from('car_houses')?.delete()?.eq('id', id);
      });
    }
  };

  // CRUD Operations for Vehicles
  const vehicles = {
    getAll: async (filters = {}) => {
      return query(async () => {
        let query = supabase?.from('vehicles')?.select(`
            *,
            car_house:car_houses(*)
          `)?.order('created_at', { ascending: false });

        if (filters?.car_house_id) {
          query = query?.eq('car_house_id', filters?.car_house_id);
        }
        if (filters?.make) {
          query = query?.eq('make', filters?.make);
        }
        if (filters?.vin) {
          query = query?.eq('vin', filters?.vin);
        }

        return await query;
      });
    },

    getByVin: async (vin) => {
      return query(async () => {
        return await supabase?.from('vehicles')?.select(`
            *,
            car_house:car_houses(*)
          `)?.eq('vin', vin)?.single();
      });
    },

    create: async (vehicleData) => {
      return query(async () => {
        return await supabase?.from('vehicles')?.insert([vehicleData])?.select()?.single();
      });
    },

    update: async (id, updates) => {
      return query(async () => {
        return await supabase?.from('vehicles')?.update(updates)?.eq('id', id)?.select()?.single();
      });
    }
  };

  // CRUD Operations for Service Items
  const serviceItems = {
    getAll: async (filters = {}) => {
      return query(async () => {
        let query = supabase?.from('service_items')?.select('*')?.order('name');

        if (filters?.is_active !== undefined) {
          query = query?.eq('is_active', filters?.is_active);
        }
              if (filters?.service_type_id) {
        query = query?.eq('service_type_id', filters?.service_type_id);
      }
        if (filters?.category) {
          query = query?.eq('category', filters?.category);
        }

        return await query;
      });
    },

    create: async (serviceItemData) => {
      return query(async () => {
        return await supabase?.from('service_items')?.insert([serviceItemData])?.select()?.single();
      });
    },

    update: async (id, updates) => {
      return query(async () => {
        return await supabase?.from('service_items')?.update(updates)?.eq('id', id)?.select()?.single();
      });
    }
  };

  // CRUD Operations for User Profiles
  const userProfiles = {
    getAll: async (filters = {}) => {
      return query(async () => {
        let query = supabase?.from('user_profiles')?.select('*')?.order('full_name');

        if (filters?.role) {
          query = query?.eq('role', filters?.role);
        }
        if (filters?.status) {
          query = query?.eq('status', filters?.status);
        }
        if (filters?.department) {
          query = query?.eq('department', filters?.department);
        }

        return await query;
      });
    },

    getCurrent: async () => {
      if (!user?.id) return { data: null, error: null };
      
      return query(async () => {
        return await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single();
      });
    },

    update: async (id, updates) => {
      return query(async () => {
        return await supabase?.from('user_profiles')?.update(updates)?.eq('id', id)?.select()?.single();
      });
    }
  };

  // CRUD Operations for Technicians
  const technicians = {
    getAll: async (filters = {}) => {
      return query(async () => {
        let query = supabase?.from('technicians')?.select('*')?.order('first_name');

        if (filters?.status) {
          query = query?.eq('status', filters?.status);
        }
        if (filters?.role) {
          query = query?.eq('role', filters?.role);
        }

        return await query;
      });
    },

    getById: async (id) => {
      return query(async () => {
        return await supabase?.from('technicians')?.select('*')?.eq('id', id)?.single();
      });
    },

    create: async (technicianData) => {
      return query(async () => {
        return await supabase?.from('technicians')?.insert([technicianData])?.select()?.single();
      });
    },

    update: async (id, updates) => {
      return query(async () => {
        return await supabase?.from('technicians')?.update(updates)?.eq('id', id)?.select()?.single();
      });
    },

    delete: async (id) => {
      return query(async () => {
        return await supabase?.from('technicians')?.delete()?.eq('id', id);
      });
    }
  };

  // Notification operations
  const notifications = {
    getAll: async (filters = {}) => {
      return query(async () => {
        let query = supabase?.from('notifications')?.select(`
            *,
            order:orders(order_number, title)
          `)?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

        if (filters?.is_read !== undefined) {
          query = query?.eq('is_read', filters?.is_read);
        }
        if (filters?.type) {
          query = query?.eq('type', filters?.type);
        }

        return await query;
      });
    },

    markAsRead: async (id) => {
      return query(async () => {
        return await supabase?.from('notifications')?.update({ is_read: true })?.eq('id', id)?.eq('user_id', user?.id);
      });
    },

    markAllAsRead: async () => {
      return query(async () => {
        return await supabase?.from('notifications')?.update({ is_read: true })?.eq('user_id', user?.id)?.eq('is_read', false);
      });
    }
  };

  // File upload operations
  const files = {
    upload: async (bucket, filePath, file, options = {}) => {
      return query(async () => {
        return await supabase?.storage?.from(bucket)?.upload(filePath, file, {
            upsert: options?.upsert || false,
            ...options
          });
      });
    },

    download: async (bucket, filePath) => {
      return query(async () => {
        return await supabase?.storage?.from(bucket)?.download(filePath);
      });
    },

    getPublicUrl: (bucket, filePath) => {
      const { data } = supabase?.storage?.from(bucket)?.getPublicUrl(filePath);
      return data?.publicUrl;
    },

    delete: async (bucket, filePaths) => {
      return query(async () => {
        return await supabase?.storage?.from(bucket)?.remove(filePaths);
      });
    }
  };

  // Real-time subscriptions
  const subscribe = (table, callback, filters = {}) => {
    const channel = supabase?.channel(`${table}_changes`)?.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: table,
        ...filters
      }, callback)?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  };

  return {
    loading,
    error,
    user,
    orders,
    carHouses,
    vehicles,
    serviceItems,
    userProfiles,
    technicians,
    notifications,
    files,
    subscribe,
    supabase // Expose supabase client for advanced usage
  };
};

export default useSupabase;